// ============================================================================
// Pipeline Widget — opportunities grouped by stage + stage_detail.
//
// Why we don't show $ values: opportunities.expected_arr / expected_mrr /
// bid_amount are NULL across every row in the live DB (verified May 2026).
// Showing "$0" or fake totals would be worse than honest counts. The widget
// flags the gap with a footnote so leadership knows the financial signal
// will appear once those columns get backfilled.
//
// Stage taxonomy (real, from live data):
//   * Pending → Onboarding (3)
//   * Active  → Confirmation (8), Pre_Invoicing (2)
//   * Won     → mostly historical post-close steps (Won, Onboarding, QBO_Setup,
//               Pre_Invoicing, Confirmation, Executive_Review). Collapsed
//               into a single "Won" bucket since it's history.
// ============================================================================

import type { OpportunityBucket } from './use-dashboard-data';
import { WidgetCard, WidgetSkeleton } from './widget-card';

interface Props {
  data: OpportunityBucket[] | null;
  loading: boolean;
}

const STAGE_LABEL: Record<string, string> = {
  Pending: 'Pending',
  Active: 'Active',
  Won: 'Won (closed)',
  Unknown: 'Unknown',
};

const STAGE_TONE: Record<
  string,
  { dot: string; text: string; chip: string }
> = {
  Pending: {
    dot: 'bg-st-warn-solid',
    text: 'text-st-warn-fg-strong',
    chip: 'bg-st-warn-bg',
  },
  Active: { dot: 'bg-st-info-solid', text: 'text-st-info-fg-strong', chip: 'bg-st-info-bg' },
  Won: {
    dot: 'bg-st-ok-solid',
    text: 'text-st-ok-fg-strong',
    chip: 'bg-st-ok-bg',
  },
  Unknown: {
    dot: 'bg-st-neutral',
    text: 'text-tcs-text-secondary',
    chip: 'bg-tcs-bg-muted',
  },
};

export function PipelineWidget({ data, loading }: Props) {
  if (loading && !data) {
    return (
      <WidgetCard title="Pipeline" minHeight={240}>
        <WidgetSkeleton lines={5} />
      </WidgetCard>
    );
  }

  if (!data) {
    return (
      <WidgetCard title="Pipeline" minHeight={240}>
        <p className="text-sm text-tcs-text-muted">No pipeline data available.</p>
      </WidgetCard>
    );
  }

  const total = data.reduce((s, b) => s + b.count, 0);
  const open = data
    .filter((b) => b.stage !== 'Won')
    .reduce((s, b) => s + b.count, 0);

  return (
    <WidgetCard
      title="Pipeline"
      subtitle={`${total} opportunities · ${open} open`}
      minHeight={240}
    >
      {data.length === 0 ? (
        <p className="text-sm text-tcs-text-muted">No opportunities recorded.</p>
      ) : (
        <ul className="space-y-1.5">
          {data.map((b) => {
            const tone = STAGE_TONE[b.stage] ?? STAGE_TONE.Unknown;
            const label = b.stage_detail
              ? `${STAGE_LABEL[b.stage] ?? b.stage} · ${humanizeDetail(b.stage_detail)}`
              : (STAGE_LABEL[b.stage] ?? b.stage);
            return (
              <li
                key={`${b.stage}-${b.stage_detail ?? '_'}`}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${tone.dot}`}
                    aria-hidden
                  />
                  <span className={`truncate ${tone.text}`}>{label}</span>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums ${tone.chip} ${tone.text}`}
                >
                  {b.count}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <p className="mt-3 text-[11px] text-tcs-text-muted">
        $ values pending — opportunities.expected_arr / bid_amount are not yet
        populated in production.
      </p>
    </WidgetCard>
  );
}

// "Pre_Invoicing" → "Pre invoicing", "QBO_Setup" → "QBO setup". Tweaks the
// underscored DB enum values for human reading without losing recognisability.
function humanizeDetail(s: string): string {
  return s
    .replace(/_/g, ' ')
    .replace(/^[a-z]/, (c) => c.toUpperCase())
    .replace(/QBO/i, 'QBO');
}
